import { z } from 'zod';

// ── COMMON ────────────────────────────────────────────────────────────────────
export const uuidSchema = z.string().uuid();
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

// ── AUTH ──────────────────────────────────────────────────────────────────────
export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  storeId: z.string().uuid().optional(),
});

export const inviteAcceptSchema = z.object({
  token: z.string().min(10),
  password: z.string().min(8),
  firstName: z.string().min(1).max(100),
  lastName: z.string().max(100).optional(),
});

// ── CUSTOMERS ─────────────────────────────────────────────────────────────────
const createCustomerFields = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().max(100).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(30).optional(),
  phone2: z.string().max(30).optional(),
  address: z.string().max(500).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(50).optional(),
  zip: z.string().max(20).optional(),
  smsOptIn: z.boolean().default(true),
  emailOptIn: z.boolean().default(true),
  preferredContact: z.enum(['sms', 'email', 'phone', 'none']).default('sms'),
  quietHoursStart: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/)
    .optional(),
  quietHoursEnd: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/)
    .optional(),
  quietHoursEnabled: z.boolean().optional(),
  languagePreference: z.string().min(2).max(10).optional(),
  marketingOptIn: z.boolean().optional(),
  surveyOptIn: z.boolean().optional(),
  reviewRequestOptIn: z.boolean().optional(),
  updateFrequency: z.enum(['every_update', 'status_change', 'ready_only']).optional(),
  referralSource: z.string().max(100).optional(),
  acquisitionSource: z
    .enum([
      'walk_in',
      'google_search',
      'social_media',
      'referral',
      'repeat_customer',
      'advertisement',
      'other',
    ])
    .optional()
    .nullable(),
  acquisitionSourceDetail: z.string().max(255).optional().nullable(),
  referredByCustomerId: z.string().uuid().optional().nullable(),
  notes: z.string().optional(),
});

export const createCustomerSchema = createCustomerFields.refine(
  (data) => data.email || data.phone,
  {
    message: 'Either email or phone is required',
    path: ['email'],
  },
);

export const updateCustomerSchema = createCustomerFields.partial();

export const updateCustomerStatusSchema = z.object({
  status: z.enum(['normal', 'vip', 'flagged']),
  reason: z.string().max(2000).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
});

export const customerDuplicatesQuerySchema = z.object({
  strategy: z
    .enum([
      'exact_email',
      'phone_and_full_name',
      'phone_family',
      'phone_shared',
      'name_and_address',
      'fuzzy_name',
      'all',
    ])
    .optional()
    .default('all'),
  minConfidence: z.enum(['high', 'medium', 'low']).optional().default('low'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const mergeCustomersSchema = z.object({
  primaryId: z.string().uuid(),
  duplicateIds: z.array(z.string().uuid()).min(1).max(9),
  fieldSelections: z.record(z.string(), z.string().uuid()).default({}),
  reason: z.string().trim().min(1).max(500),
});

export type MergeCustomersInput = z.infer<typeof mergeCustomersSchema>;
export type CustomerDuplicatesQuery = z.infer<typeof customerDuplicatesQuerySchema>;

export const bulkMergeCustomersSchema = z
  .object({
    customerIds: z.array(z.string().uuid()).min(2).max(100),
    primaryId: z.string().uuid(),
    survivorStrategy: z
      .enum(['manual', 'most_tickets', 'oldest', 'most_complete'])
      .default('most_tickets'),
    fieldStrategy: z.enum(['primary_wins', 'fill_blanks']).default('fill_blanks'),
    reason: z.string().trim().min(1).max(500),
    managerOverrideAuthorizedBy: z.string().uuid(),
  })
  .superRefine((val, ctx) => {
    if (!val.customerIds.includes(val.primaryId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'primaryId must be included in customerIds',
        path: ['primaryId'],
      });
    }
  });

/** Preview uses the same shape without manager PIN. */
export const bulkMergePreviewSchema = z
  .object({
    customerIds: z.array(z.string().uuid()).min(2).max(100),
    primaryId: z.string().uuid(),
    survivorStrategy: z
      .enum(['manual', 'most_tickets', 'oldest', 'most_complete'])
      .default('most_tickets'),
    fieldStrategy: z.enum(['primary_wins', 'fill_blanks']).default('fill_blanks'),
    reason: z.string().trim().max(500).optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.customerIds.includes(val.primaryId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'primaryId must be included in customerIds',
        path: ['primaryId'],
      });
    }
  });

export type BulkMergeCustomersInput = z.infer<typeof bulkMergeCustomersSchema>;
export type BulkMergePreviewInput = z.infer<typeof bulkMergePreviewSchema>;

export const adjustCustomerStoreCreditSchema = z.object({
  /** Signed dollar amount: positive issues credit, negative deducts. */
  amount: z.number().refine((n) => n !== 0 && Number.isFinite(n), { message: 'Amount must be a non-zero number' }),
  reason: z.string().trim().min(1).max(300),
  notes: z.string().trim().max(500).optional().nullable(),
});

export const adjustCustomerLoyaltyPointsSchema = z.object({
  /** Signed points delta: positive awards, negative deducts. */
  delta: z.number().int().refine((n) => n !== 0, { message: 'Delta must be a non-zero integer' }),
  reason: z.string().trim().min(1).max(300),
  notes: z.string().trim().max(300).optional().nullable(),
});

export const customerLedgerQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});


export const customerSearchSchema = z.object({
  q: z.string().max(100).default(''),
});

export const customerListQuerySchema = customerSearchSchema.merge(paginationSchema).extend({
  includeArchived: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .optional()
    .transform((v) => v === true || v === 'true' || v === '1'),
  idsOnly: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .optional()
    .transform((v) => v === true || v === 'true' || v === '1'),
});

export const archiveCustomerSchema = z.object({
  reason: z.string().trim().max(500).optional().nullable(),
});

export const bulkArchiveCustomersSchema = z.object({
  customerIds: z.array(z.string().uuid()).min(1).max(100),
  reason: z.string().trim().max(500).optional().nullable(),
  /** When true, return per-id outcomes without mutating. */
  dryRun: z.boolean().optional().default(false),
});

export const bulkUnarchiveCustomersSchema = z.object({
  customerIds: z.array(z.string().uuid()).min(1).max(100),
  dryRun: z.boolean().optional().default(false),
});

export const deleteCustomerSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  confirmName: z.string().trim().min(1).max(200),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

export const anonymizeCustomerSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  confirmText: z.literal('ANONYMIZE'),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

export const bulkDeleteCustomersSchema = z
  .object({
    customerIds: z.array(z.string().uuid()).min(1).max(50),
    reason: z.string().trim().min(3).max(500),
    confirmText: z.literal('DELETE'),
    managerOverrideAuthorizedBy: z.string().uuid().optional(),
    dryRun: z.boolean().optional().default(false),
  })
  .superRefine((val, ctx) => {
    if (!val.dryRun && !val.managerOverrideAuthorizedBy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Manager override is required',
        path: ['managerOverrideAuthorizedBy'],
      });
    }
  });

export type ArchiveCustomerInput = z.infer<typeof archiveCustomerSchema>;
export type DeleteCustomerInput = z.infer<typeof deleteCustomerSchema>;
export type AnonymizeCustomerInput = z.infer<typeof anonymizeCustomerSchema>;
export type BulkArchiveCustomersInput = z.infer<typeof bulkArchiveCustomersSchema>;
export type BulkDeleteCustomersInput = z.infer<typeof bulkDeleteCustomersSchema>;

// ── INVENTORY ARCHIVE / BULK ──────────────────────────────────────────────────
export const inventoryListStatusSchema = z.enum(['active', 'archived', 'discontinued', 'all']);

export const archiveInventoryItemSchema = z.object({
  reason: z.string().trim().max(500).optional().nullable(),
  /** Required when qty_on_hand > 0 and writeOffStock is not true */
  acknowledgeStock: z.boolean().optional().default(false),
  /** When true, zero stock via inventory_adjustments (reason archived) then archive */
  writeOffStock: z.boolean().optional().default(false),
});

export const bulkArchiveInventorySchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1).max(100),
  reason: z.string().trim().max(500).optional().nullable(),
  acknowledgeStock: z.boolean().optional().default(false),
  writeOffStock: z.boolean().optional().default(false),
  dryRun: z.boolean().optional().default(false),
});

export const bulkUnarchiveInventorySchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1).max(100),
  dryRun: z.boolean().optional().default(false),
});

export const bulkDiscontinueInventorySchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1).max(100),
  dryRun: z.boolean().optional().default(false),
});

export const deleteInventoryItemSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  confirmName: z.string().trim().min(1).max(255),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

export const bulkDeleteInventorySchema = z
  .object({
    itemIds: z.array(z.string().uuid()).min(1).max(50),
    reason: z.string().trim().min(3).max(500),
    confirmText: z.literal('DELETE'),
    managerOverrideAuthorizedBy: z.string().uuid().optional(),
    dryRun: z.boolean().optional().default(false),
  })
  .superRefine((val, ctx) => {
    if (!val.dryRun && !val.managerOverrideAuthorizedBy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Manager override is required',
        path: ['managerOverrideAuthorizedBy'],
      });
    }
  });

export type ArchiveInventoryItemInput = z.infer<typeof archiveInventoryItemSchema>;
export type BulkArchiveInventoryInput = z.infer<typeof bulkArchiveInventorySchema>;
export type DeleteInventoryItemInput = z.infer<typeof deleteInventoryItemSchema>;
export type BulkDeleteInventoryInput = z.infer<typeof bulkDeleteInventorySchema>;

// ── TICKETS ───────────────────────────────────────────────────────────────────
export const createTicketDeviceSchema = z.object({
  makeId: z.string().uuid().optional(),
  modelId: z.string().uuid().optional(),
  customModelName: z.string().max(150).optional(),
  // Digits only, up to 15 — no Luhn gate so staff can save IMEI without lookup
  imei: z
    .string()
    .max(15)
    .regex(/^\d*$/, { message: 'IMEI must be digits only' })
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined)),
  imei2: z
    .string()
    .max(15)
    .regex(/^\d*$/, { message: 'IMEI must be digits only' })
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined)),
  serialNumber: z.string().max(100).optional(),
  color: z.string().max(50).optional(),
  storageGb: z.number().int().optional(),
  conditionAtIntake: z.enum(['excellent', 'good', 'fair', 'poor', 'broken']).default('good'),
  conditionNotes: z.string().optional(),
  passcode: z.string().max(100).optional(),
  passcodeType: z
    .enum(['pin', 'pattern', 'password', 'face_id', 'fingerprint', 'none', 'unknown'])
    .default('unknown'),
  accessoriesReceived: z.array(z.string()).default([]),
});

export const createTicketRepairSchema = z.object({
  ticketDeviceId: z.string().uuid(),
  serviceId: z.string().uuid().optional(),
  serviceName: z.string().min(1).max(200),
  description: z.string().optional(),
  quotedPrice: z.number().nonnegative(),
  assignedTechId: z.string().uuid().optional(),
  warrantyDays: z.number().int().nonnegative().optional(),
  warrantyType: z.enum(['days', 'lifetime', 'none']).optional(),
});

/** Repairs at ticket create time — link to `devices[deviceIndex]`. */
export const createTicketRepairAtIntakeSchema = z.object({
  deviceIndex: z.number().int().min(0),
  serviceId: z.string().uuid().optional(),
  serviceName: z.string().min(1).max(200),
  description: z.string().optional(),
  quotedPrice: z.number().nonnegative(),
  assignedTechId: z.string().uuid().optional(),
  warrantyDays: z.number().int().nonnegative().optional(),
  warrantyType: z.enum(['days', 'lifetime', 'none']).optional(),
});

export const createTicketLineItemSchema = z.object({
  ticketRepairId: z.string().uuid().optional(),
  type: z.enum(['part', 'retail', 'custom', 'fee', 'discount', 'labor', 'repair']),
  inventoryItemId: z.string().uuid().optional(),
  serialId: z.string().uuid().optional(),
  description: z.string().min(1).max(300),
  quantity: z.number().positive().default(1),
  unitPrice: z.number().nonnegative(),
  discountAmount: z.number().nonnegative().default(0),
  discountPct: z.number().nonnegative().max(100).default(0),
  discountReason: z.string().max(200).optional(),
  discountPresetId: z.string().uuid().optional().nullable(),
  taxExempt: z.boolean().default(false),
  managerOverrideAuthorizedBy: z.string().uuid().optional(),
});

export const collectDepositSchema = z.object({
  amount: z.number().positive(),
  method: z.enum(['cash', 'card', 'store_credit']),
  cashTendered: z.number().positive().optional(),
  notes: z.string().max(300).optional(),
});

export const createTicketSchema = z
  .object({
    intakeType: z.enum(['walk_in', 'drop_off', 'quote']),
    source: z
      .enum(['walk_in', 'phone', 'online', 'referral', 'warranty', 'pos_sale'])
      .default('walk_in'),
    priority: z.enum(['normal', 'rush', 'urgent']).default('normal'),

    // Customer — one of these must be provided
    customerId: z.string().uuid().optional(),
    customerNameOverride: z.string().max(200).optional(),
    customerPhoneOverride: z.string().max(30).optional(),
    customerEmailOverride: z.string().email().optional(),

    // Devices and repairs
    devices: z.array(createTicketDeviceSchema).default([]),
    repairs: z.array(createTicketRepairAtIntakeSchema),
    warrantyOfTicketId: z.string().uuid().optional(),

    // Financials
    promisedDate: z.string().datetime().optional(),
    intakeNotes: z.string().optional(),
    customerComplaint: z.string().max(500).optional(),
    depositAmount: z.number().nonnegative().optional(),
    tags: z.array(z.string()).default([]),
  })
  .refine(
    (data) =>
      data.customerId ||
      data.customerNameOverride ||
      (data.intakeType === 'walk_in' && data.devices.length === 0 && data.repairs.length === 0),
    { message: 'Either customerId or customerNameOverride is required', path: ['customerId'] },
  )
  .superRefine((data, ctx) => {
    if (data.repairs.length > 0 && data.devices.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one device is required when repairs are present',
        path: ['devices'],
      });
    }
    data.repairs.forEach((r, i) => {
      if (r.deviceIndex >= data.devices.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['repairs', i, 'deviceIndex'],
          message: `deviceIndex must be less than devices.length (${data.devices.length})`,
        });
      }
    });
  });

export const updateTicketStatusSchema = z.object({
  /** Slug from ticket_statuses catalog (system or custom). */
  status: z.string().min(1).max(50),
  reason: z.string().max(500).optional(),
  sendNotification: z.boolean().default(true),
  managerOverrideAuthorizedBy: z.string().uuid().optional(),
  /** Clear a sticky manual override so device/repair cascade can drive ticket status again. */
  clearStatusOverride: z.boolean().optional(),
});

export const deleteTicketSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  confirmTicketNumber: z.string().trim().min(1).max(30),
  managerOverrideAuthorizedBy: z.string().uuid(),
  /** Required when ticket has non-void payments (unless RepairDesk import). */
  acknowledgePayments: z.boolean().optional(),
});

export const bulkDeleteTicketsSchema = z.object({
  ticketIds: z.array(z.string().uuid()).min(1).max(50),
  reason: z.string().trim().min(3).max(500),
  confirmText: z.literal('DELETE'),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

export const addTicketNoteSchema = z.object({
  body: z.string().min(1).max(2000),
  visibility: z.enum(['public', 'private']).default('private'),
  pinned: z.boolean().default(false),
  attachments: z
    .array(
      z.object({
        url: z.string().url(),
        filename: z.string(),
        mimeType: z.string(),
      }),
    )
    .default([]),
});

export const assignTicketSchema = z.object({
  technicianId: z.string().uuid(),
});

export const ticketFilterSchema = z
  .object({
    status: z.string().optional(),
    priority: z.string().optional(),
    assignedTo: z.union([z.string().uuid(), z.literal('unassigned')]).optional(),
    storeId: z.string().uuid().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    createdFrom: z.string().datetime().optional(),
    createdTo: z.string().datetime().optional(),
    dueFrom: z.string().datetime().optional(),
    dueTo: z.string().datetime().optional(),
    source: z.string().optional(),
    excludeSource: z.string().optional(),
    search: z.string().max(100).optional(),
    customerId: z.string().uuid().optional(),
    modelId: z.string().uuid().optional(),
    customModelName: z.string().max(150).optional(),
    tags: z.array(z.string()).optional(),
    overdue: z.coerce.boolean().optional(),
    overdueOnly: z.coerce.boolean().optional(),
    assignedToMe: z.coerce.boolean().optional(),
    warrantyOnly: z.coerce.boolean().optional(),
    /** Filter by import source. Use `native` or `none` for tickets with no importedFrom. */
    importedFrom: z.string().max(50).optional(),
    sort: z.enum(['priority', 'newest', 'oldest', 'due_date', 'customer']).default('priority'),
    page: z.coerce.number().int().min(1).default(1),
    // Kanban fetches up to 500 active tickets in one request
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
  });

export const bulkUpdateTicketsSchema = z
  .object({
    ticketIds: z.array(z.string().uuid()).min(1).max(100),
    status: z
      .enum([
        'quote',
        'intake',
        'pending_sig',
        'in_queue',
        'diagnosing',
        'waiting_auth',
        'waiting_parts',
        'in_repair',
        'on_hold',
        'ready',
        'pending_pay',
        'closed',
        'warranty',
        'cancelled',
        'voided',
      ])
      .optional(),
    assignedTo: z.string().uuid().nullable().optional(),
  })
  .refine((d) => d.status !== undefined || d.assignedTo !== undefined, {
    message: 'Provide status and/or assignedTo',
  });

export const createTimeEntrySchema = z
  .object({
    ticketRepairId: z.string().uuid().nullable().optional(),
    entryType: z.enum(['timer', 'manual']),
    startedAt: z.string().datetime().optional(),
    endedAt: z.string().datetime().optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
    billable: z.boolean().default(true),
    note: z.string().max(500).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.entryType === 'timer') {
      if (!data.startedAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'startedAt is required for timer entries',
          path: ['startedAt'],
        });
      }
      if (data.endedAt == null && data.durationSeconds != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Open timer must not include durationSeconds',
          path: ['durationSeconds'],
        });
      }
      return;
    }
    const hasDuration = data.durationSeconds != null && data.durationSeconds > 0;
    const hasRange = Boolean(data.startedAt && data.endedAt);
    if (!hasDuration && !hasRange) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Manual entry requires durationSeconds or both startedAt and endedAt',
        path: ['durationSeconds'],
      });
    }
  });

export const patchTimeEntrySchema = z
  .object({
    endedAt: z.string().datetime().nullable().optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
    billable: z.boolean().optional(),
    note: z.string().max(500).nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'At least one field is required' });

/** @deprecated use createTimeEntrySchema */
export const logTimeEntrySchema = createTimeEntrySchema;

export const publicCustomerInfoSchema = z.object({
  storeId: z.string().uuid(),
  sessionToken: z.string().uuid(),
  customerInfo: z.object({
    firstName: z.string().min(1).max(100),
    lastName: z.string().max(100).optional(),
    phone: z.string().min(7).max(30),
    email: z.string().email().optional(),
    address: z.string().max(500).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(50).optional(),
    zip: z.string().max(20).optional(),
  }),
});

export const publicWaiverSignSchema = z.object({
  displayToken: z.string().uuid(),
  signatureDataUrl: z
    .string()
    .min(1)
    .max(25_000_000)
    .regex(/^data:image\/(png|jpeg|webp);base64,/i, {
      message: 'signatureDataUrl must be a base64 data URL (image/png, image/jpeg, or image/webp)',
    }),
  waiverType: z.enum(['pre_repair', 'post_repair']),
  waiverVersion: z.string().min(1).max(20),
});

export const signTicketWaiverSchema = z.object({
  waiverType: z.enum(['pre_repair', 'post_repair']),
  signatureDataUrl: z
    .string()
    .min(1)
    .max(25_000_000)
    .regex(/^data:image\/(png|jpeg|webp);base64,/i, {
      message: 'signatureDataUrl must be a base64 data URL (image/png, image/jpeg, or image/webp)',
    }),
  waiverVersion: z.string().min(1).max(20),
  collectionMode: z.enum(['hand_across', 'customer_display', 'sms_link']),
});

// ── CHECKOUT ──────────────────────────────────────────────────────────────────
export const checkoutPaymentSchema = z.object({
  method: z.enum(['card', 'cash', 'store_credit', 'loyalty_points', 'check', 'other']),
  amount: z.number().positive(),
  tipAmount: z.number().nonnegative().default(0),
  /** Business payment date/time (ISO). Defaults to now. Backdating requires manager override. */
  paidAt: z.string().datetime({ offset: true }).or(z.string().min(1)).optional(),
  /** Required when paidAt falls inside a closed cash session. */
  acknowledgeClosedSession: z.boolean().optional(),
  // Manual card entry
  transactionId: z.string().max(255).optional(),
  cardType: z
    .enum(['visa', 'mastercard', 'amex', 'discover', 'debit', 'other'])
    .optional(),
  lastFour: z
    .string()
    .regex(/^\d{4}$/, { message: 'lastFour must be 4 digits' })
    .optional(),
  // Cash
  cashTendered: z.number().positive().optional(),
  // Notes
  notes: z.string().max(300).optional(),
  /** Staff PIN (own identity) required when tips_require_pin and tipAmount > 0 */
  tipPin: z.string().min(4).max(12).optional(),
  managerOverrideAuthorizedBy: z.string().uuid().optional(),
});

export const checkoutSchema = z.object({
  ticketId: z.string().uuid(),
  payments: z.array(checkoutPaymentSchema).min(1),
  storeCreditApplied: z.number().nonnegative().default(0),
  loyaltyPointsRedeemed: z.number().int().nonnegative().default(0),
  receiptMethod: z.enum(['print', 'email', 'sms', 'none']).default('none'),
  postWaiverSigned: z.boolean().default(false),
  postWaiverSignatureUrl: z.string().url().optional(),
});

// ── PAYMENT EDIT / VOID ───────────────────────────────────────────────────────
export const editPaymentSchema = z.object({
  amount: z.number().positive().optional(),
  method: z.enum(['card', 'cash', 'store_credit', 'loyalty_points', 'check', 'other']).optional(),
  paidAt: z.string().datetime({ offset: true }).or(z.string().min(1)).optional(),
  acknowledgeClosedSession: z.boolean().optional(),
  transactionId: z.string().max(255).optional().nullable(),
  cardType: z
    .enum(['visa', 'mastercard', 'amex', 'discover', 'debit', 'other'])
    .optional()
    .nullable(),
  lastFour: z
    .string()
    .regex(/^\d{4}$/, { message: 'lastFour must be 4 digits' })
    .optional()
    .nullable(),
  notes: z.string().max(300).optional().nullable(),
  editNotes: z.string().min(1).max(1000),
  managerOverrideAuthorizedBy: z.string().uuid().optional(),
});

export const voidPaymentSchema = z.object({
  reason: z.string().min(1).max(1000),
  ticketAction: z.enum(['reopen', 'keep_closed']),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

// ── POS SALES / RETURNS ───────────────────────────────────────────────────────
export const listSalesQuerySchema = paginationSchema.extend({
  search: z.string().max(200).optional(),
  dateFrom: z.string().min(1).max(40).optional(),
  dateTo: z.string().min(1).max(40).optional(),
  method: z.enum(['cash', 'card', 'store_credit', 'loyalty_points', 'check', 'other']).optional(),
  staffId: z.string().uuid().optional(),
});

export const createSaleReturnSchema = z.object({
  items: z
    .array(
      z.object({
        lineItemId: z.string().uuid(),
        quantity: z.number().positive(),
        returnToStock: z.boolean(),
        condition: z.enum(['good', 'damaged']),
        conditionNotes: z.string().max(500).optional(),
      }),
    )
    .min(1),
  refundMethod: z.enum(['cash', 'card', 'store_credit']),
  refundAmount: z.number().positive(),
  transactionId: z.string().max(255).optional(),
  notes: z.string().max(1000).optional(),
  managerOverrideAuthorizedBy: z.string().uuid(),
});

export type EditPaymentInput = z.infer<typeof editPaymentSchema>;
export type VoidPaymentInput = z.infer<typeof voidPaymentSchema>;
export type ListSalesQuery = z.infer<typeof listSalesQuerySchema>;
export type CreateSaleReturnInput = z.infer<typeof createSaleReturnSchema>;

// ── INVENTORY ─────────────────────────────────────────────────────────────────
export const createInventoryItemSchema = z.object({
  name: z.string().min(1).max(255),
  category: z.enum(['repair_part', 'retail_accessory', 'trade_in', 'refurbished']),
  subcategoryId: z.string().uuid().optional(),
  description: z.string().optional(),
  retailPrice: z.number().nonnegative(),
  taxable: z.boolean().default(true),
  serialized: z.boolean().default(false),
  trackStock: z.boolean().default(true),
  reorderPointDefault: z.number().int().nonnegative().default(2),
  reorderQtyDefault: z.number().int().positive().default(10),
  warrantyDaysDefault: z.number().int().nonnegative().default(90),
  loyaltyOverride: z.enum(['inherit', 'custom_rate', 'disabled']).default('inherit'),
  loyaltyEarnRate: z.number().nonnegative().optional(),
  tags: z.array(z.string()).default([]),
  compatibleMakeIds: z.array(z.string().uuid()).default([]),
  compatibleModelIds: z.array(z.string().uuid()).default([]),
  compatibleMakes: z
    .array(z.object({ makeId: z.string().uuid(), makeName: z.string().min(1).max(100) }))
    .optional(),
  compatibleModels: z
    .array(
      z.object({
        modelId: z.string().uuid(),
        modelName: z.string().min(1).max(200),
        makeId: z.string().uuid().optional(),
        makeName: z.string().max(100).optional(),
      }),
    )
    .optional(),
  inventoryCategoryId: z.string().uuid().nullable().optional(),
  condition: z.enum(['new', 'used', 'refurbished', 'oem', 'aftermarket']).optional(),
  upc: z.string().max(100).nullable().optional(),
  manufacturerPartNumber: z.string().max(100).nullable().optional(),
  color: z.string().max(100).nullable().optional(),
});

export const manualAdjustmentSchema = z.object({
  itemId: z.string().uuid(),
  delta: z.number().refine((n) => n !== 0, { message: 'Must be non-zero' }),
  reasonCode: z.enum([
    'found',
    'damaged',
    'theft',
    'data_entry_error',
    'vendor_credit',
    'opening_balance',
    'warranty_return',
    'expired',
    'other',
  ]),
  reasonNotes: z.string().max(500).optional(),
  /** When set to po_receive with positive delta, creates a FIFO layer at unitCost (quick receive). */
  adjustmentType: z.enum(['manual', 'po_receive']).default('manual'),
  unitCost: z.number().nonnegative().optional(),
  supplierVariantId: z.string().uuid().optional(),
});

export const createInventorySerialSchema = z
  .object({
    serialNumber: z.string().max(100).optional(),
    imei: z.string().length(15).optional(),
    conditionGrade: z.enum(['new', 'a', 'b', 'c', 'f']).optional(),
    internalNotes: z.string().optional(),
  })
  .refine((b) => Boolean(b.serialNumber?.trim() || b.imei?.trim()), {
    message: 'Serial number or IMEI required',
  });

export const createCycleCountSchema = z.object({
  countType: z.enum(['full', 'category', 'location', 'custom']),
  filterCategory: z.enum(['repair_part', 'retail_accessory', 'trade_in', 'refurbished']).optional(),
  filterBinLocation: z.string().max(100).optional(),
  blindCount: z.boolean().default(true),
  notes: z.string().optional(),
  itemIds: z.array(z.string().uuid()).optional(),
});

export const patchCycleCountLineSchema = z.object({
  countedQty: z.number().nonnegative(),
  notes: z.string().max(300).optional(),
});

export const createInventoryTransferSchema = z.object({
  toStoreId: z.string().uuid(),
  notes: z.string().optional(),
  lines: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        qtyRequested: z.number().int().positive(),
        serialId: z.string().uuid().optional(),
      }),
    )
    .min(1),
});

export const patchPurchaseOrderSchema = z
  .object({
    trackingNumber: z.string().max(255).nullable().optional(),
    supplierRef: z.string().max(100).nullable().optional(),
    notes: z.string().nullable().optional(),
    expectedAt: z.string().datetime().nullable().optional(),
  })
  .strict()
  .refine((o) => Object.keys(o).length > 0, { message: 'At least one field required' });

export const createPurchaseOrderSchema = z.object({
  supplierId: z.string().uuid(),
  expectedAt: z.string().datetime().optional(),
  shippingCost: z.number().nonnegative().default(0),
  notes: z.string().optional(),
  lines: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        supplierVariantId: z.string().uuid().optional(),
        qtyOrdered: z.number().int().positive(),
        unitCost: z.number().nonnegative(),
        notes: z.string().max(300).optional(),
        linkedTicketId: z.string().uuid().optional(),
      }),
    )
    .min(1),
});

export const receivePoSchema = z.object({
  lines: z.array(
    z.object({
      poLineId: z.string().uuid(),
      qtyReceived: z.number().int().nonnegative(),
      actualUnitCost: z.number().nonnegative().optional(),
      serials: z
        .array(
          z.object({
            serialNumber: z.string().max(100).optional(),
            imei: z.string().length(15).optional(),
          }),
        )
        .optional(),
    }),
  ),
});

export const gradingSchema = z.object({
  conditionGrade: z.enum(['new', 'a', 'b', 'c', 'f']),
  color: z.string().max(50).optional(),
  storageGb: z.number().int().optional(),
  carrier: z.string().max(50).optional(),
  gradingNotes: z.string().optional(),
  functionTestResults: z.record(z.string(), z.enum(['pass', 'fail', 'na'])).default({}),
  dataWipeMethod: z.string().max(50).optional(),
  retailPrice: z.number().nonnegative(),
  binLocation: z.string().max(100).optional(),
});

// Export all schemas for use across apps
export type CreateTicketInput = z.infer<typeof createTicketSchema>;
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;
export type CreateInventoryItemInput = z.infer<typeof createInventoryItemSchema>;
export type CheckoutInput = z.infer<typeof checkoutSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type TicketFilterInput = z.infer<typeof ticketFilterSchema>;

export {
  LABEL_SIZE_PRESET_IDS,
  LABEL_SIZE_PRESET_OPTIONS,
  resolveLabelDimensions,
} from './label-printing.js';
export type { LabelSizePresetId } from './label-printing.js';

export {
  INVENTORY_LABEL_SIZE_PRESET_IDS,
  INVENTORY_LABEL_SIZE_PRESET_OPTIONS,
  INVENTORY_BARCODE_SOURCES,
  INVENTORY_BARCODE_FORMATS,
  INVENTORY_PRICE_SOURCES,
  DEFAULT_INVENTORY_LABEL_CONFIG,
  resolveInventoryLabelDimensions,
  mergeInventoryLabelConfig,
  inventoryLabelConfigSchema,
} from './inventory-label-printing.js';
export type {
  InventoryLabelSizePresetId,
  InventoryLabelConfig,
  InventoryBarcodeSource,
  InventoryBarcodeFormat,
  InventoryPriceSource,
} from './inventory-label-printing.js';

export {
  walkInQueueReasons,
  walkInQueueStatuses,
  queueDisplayNameModes,
  createWalkInQueueSchema,
  updateWalkInQueueSchema,
  reorderWalkInQueueSchema,
} from './walk-in-queue.js';
export type {
  WalkInQueueReason,
  WalkInQueueStatus,
  QueueDisplayNameMode,
} from './walk-in-queue.js';

// ── CUSTOMER PORTAL ───────────────────────────────────────────────────────────
export const portalLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  /** Tenant slug — optional if the portal email is unique across tenants. */
  tenantSlug: z.string().min(1).max(100).optional(),
});

export const portalForgotPasswordSchema = z.object({
  email: z.string().email(),
  tenantSlug: z.string().min(1).max(100).optional(),
});

export const portalResetPasswordSchema = z.object({
  token: z.string().min(10),
  newPassword: z.string().min(8).max(128),
});

export const portalChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8).max(128),
});

// ── STAFF PROFILE (self-service) ─────────────────────────────────────────────
export const staffChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z
    .string()
    .min(8, 'At least 8 characters')
    .max(128)
    .regex(/[0-9]/, 'At least one number')
    .regex(/[A-Z]/, 'At least one uppercase letter'),
});

export const updateMyProfileSchema = z
  .object({
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
    email: z.string().email().optional(),
    phone: z.string().max(30).nullable().optional(),
    avatarUrl: z.string().url().max(500).nullable().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'At least one field required' });

export const userPreferencesSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']).optional(),
  defaultTicketView: z.enum(['list', 'cards', 'kanban']).optional(),
  defaultQueueFilter: z.enum(['all_active', 'my_tickets', 'unassigned']).optional(),
  defaultReportPeriod: z.enum(['today', 'week', 'month', 'quarter', 'year']).optional(),
  defaultIntakeType: z.enum(['walk_in', 'drop_off', 'mail_in']).optional(),
  autoAssignToMe: z.boolean().optional(),
  promisedTimeOffset: z.enum(['same_day', 'next_day', 'calculator']).optional(),
  language: z.string().max(10).optional(),
  ticketDetailView: z.enum(['tech', 'full']).optional(),
});

export const notificationPrefsSchema = z.object({
  inApp: z.record(z.string(), z.boolean()).optional(),
  email: z.record(z.string(), z.boolean()).optional(),
  sms: z.record(z.string(), z.boolean()).optional(),
  emailOverride: z.string().email().nullable().optional(),
  smsOverride: z.string().max(30).nullable().optional(),
});

export type UpdateMyProfileInput = z.infer<typeof updateMyProfileSchema>;
export type UserPreferencesInput = z.infer<typeof userPreferencesSchema>;
export type NotificationPrefsInput = z.infer<typeof notificationPrefsSchema>;
export type StaffChangePasswordInput = z.infer<typeof staffChangePasswordSchema>;

export const createPortalAccountSchema = z.object({
  portalEmail: z.string().email().optional(),
  canCreateTickets: z.boolean().optional(),
});

export const updatePortalSettingsSchema = z.object({
  canCreateTickets: z.boolean().optional(),
  portalEnabled: z.boolean().optional(),
});

export const portalTicketListQuerySchema = paginationSchema.extend({
  status: z.string().optional(),
});

export const portalCreateTicketSchema = z.object({
  deviceMake: z.string().min(1).max(100),
  deviceModel: z.string().min(1).max(150),
  issueDescription: z.string().min(1).max(2000),
  preferredDate: z.string().datetime().optional(),
  additionalNotes: z.string().max(2000).optional(),
});

export const portalAddNoteSchema = z.object({
  message: z.string().min(1).max(2000),
});

export const estimateServiceLineSchema = z.object({
  serviceName: z.string().min(1).max(200),
  price: z.coerce.number().nonnegative(),
  serviceId: z.string().uuid().optional(),
});

export const estimatePartLineSchema = z.object({
  partName: z.string().min(1).max(200),
  price: z.coerce.number().nonnegative(),
});

export const createEstimateSchema = z.object({
  ticketId: z.string().uuid(),
  services: z.array(estimateServiceLineSchema).min(1),
  parts: z.array(estimatePartLineSchema).optional(),
  notes: z.string().max(5000).optional(),
  validDays: z.coerce.number().positive().max(30).optional(),
  requiresSignature: z.boolean().optional(),
  sendVia: z.enum(['email', 'sms', 'both', 'none']).optional(),
});

export const updateEstimateSchema = z.object({
  services: z.array(estimateServiceLineSchema).min(1).optional(),
  parts: z.array(estimatePartLineSchema).optional(),
  notes: z.string().max(5000).nullable().optional(),
  validDays: z.coerce.number().positive().max(30).optional(),
  requiresSignature: z.boolean().optional(),
});

export const sendEstimateSchema = z.object({
  via: z.enum(['email', 'sms', 'both']),
});

export const approveEstimateSchema = z.object({
  signatureDataUrl: z.string().max(500_000).optional(),
  approvedByName: z.string().min(1).max(255),
});

export const declineEstimateSchema = z.object({
  reason: z.string().max(2000).optional(),
});

export const publicTrackQuerySchema = z.object({
  phone: z.string().min(3).max(30),
  ticketNumber: z.string().max(30).optional(),
  /** Optional — used for iframe embeds (`?store=slug`). Phone lookup works without it. */
  tenantSlug: z.string().min(1).max(100).optional(),
});

export type PortalLoginInput = z.infer<typeof portalLoginSchema>;
export type CreateEstimateInput = z.infer<typeof createEstimateSchema>;

// ── SPRINT 8 — SUBSCRIPTIONS, TRANSFERS, MIGRATION ───────────────────────────
export const createSubscriptionSchema = z.object({
  customerId: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  amount: z.coerce.number().positive(),
  frequency: z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly']).default('monthly'),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  nextBillingDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  autoCharge: z.boolean().optional(),
  notes: z.string().max(5000).optional(),
});

export const collectSubscriptionPaymentSchema = z.object({
  method: z.enum(['cash', 'card', 'store_credit']),
  cashTendered: z.coerce.number().positive().optional(),
  notes: z.string().max(300).optional(),
});

export const transferTicketSchema = z.object({
  destinationStoreId: z.string().uuid(),
  reason: z.string().max(2000).optional(),
  keepOriginal: z.boolean().default(false),
  notifyCustomer: z.boolean().default(false),
});

export const cloneTicketSchema = z.object({
  customerId: z.string().uuid().optional().nullable(),
  includeRepairs: z.boolean().default(true),
  includeDevices: z.boolean().default(true),
  includeNotes: z.boolean().default(false),
  intakeType: z.enum(['walk_in', 'drop_off', 'quote']).optional(),
  notes: z.string().max(2000).optional().nullable(),
});

export const evaluateUpsellSchema = z.object({
  ticketId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional().nullable(),
  showAt: z.enum(['ticket_create', 'checkout']),
  devices: z
    .array(
      z.object({
        modelId: z.string().uuid().optional().nullable(),
        makeName: z.string().max(150).optional().nullable(),
        modelName: z.string().max(150).optional().nullable(),
        customModelName: z.string().max(150).optional().nullable(),
      }),
    )
    .optional(),
  repairs: z
    .array(
      z.object({
        serviceName: z.string().min(1).max(200),
        quotedPrice: z.coerce.number().nonnegative(),
      }),
    )
    .optional(),
});

export const upsellInteractSchema = z.object({
  suggestionId: z.string().uuid(),
  ticketId: z.string().uuid().optional().nullable(),
  customerId: z.string().uuid().optional().nullable(),
  outcome: z.enum(['acted_on', 'dismissed', 'remind_at_checkout', 'not_applicable']),
  revenueAdded: z.coerce.number().nonnegative().optional(),
});

export const createUpsellSuggestionSchema = z.object({
  name: z.string().min(1).max(200),
  triggerType: z.enum([
    'device_age',
    'device_age_with_repair_type',
    'repeat_device_repair',
    'repair_type_match',
    'repair_cost_vs_value',
    'customer_lifetime_value',
    'customer_last_visit',
    'no_protective_case',
    'custom_rule',
  ]),
  triggerConfig: z.record(z.unknown()).default({}),
  title: z.string().min(1).max(200),
  message: z.string().min(1).max(5000),
  actionLabel: z.string().max(100).optional().nullable(),
  actionType: z.enum(['add_service', 'add_inventory', 'note_only', 'custom']).optional().nullable(),
  actionConfig: z.record(z.unknown()).optional().nullable(),
  showAt: z.enum(['ticket_create', 'checkout']).default('checkout'),
  priority: z.coerce.number().int().min(0).max(999).default(50),
  active: z.boolean().default(true),
  storeId: z.string().uuid().optional().nullable(),
});

export const patchUpsellSuggestionSchema = createUpsellSuggestionSchema.partial().extend({
  delete: z.boolean().optional(),
});

export const switchStoreSchema = z.object({
  storeId: z.string().uuid(),
});

export const migrationValidateSchema = z.object({
  type: z.enum(['customers', 'tickets']),
  csvContent: z.string().min(1),
  mappings: z.record(z.string()).optional(),
});

export const migrationCustomersImportOptionsSchema = z.object({
  onDuplicate: z.enum(['skip', 'update']).default('skip'),
});

export const migrationInventoryImportOptionsSchema = z.object({
  onDuplicate: z.enum(['skip', 'update']).default('skip'),
  createMissingCategories: z.coerce.boolean().default(true),
  createMissingSuppliers: z.coerce.boolean().default(true),
  createMissingDevices: z.coerce.boolean().default(true),
});

export const migrationInventoryBulkOptionsSchema = z.object({
  allowTrackInventory: z.coerce.boolean().default(false),
  allowCondition: z.coerce.boolean().default(false),
  allowArchived: z.coerce.boolean().default(false),
  createMissingDevices: z.coerce.boolean().default(false),
  createMissingSuppliers: z.coerce.boolean().default(false),
});

export const migrationServicesImportOptionsSchema = z.object({
  onDuplicate: z
    .enum(['skip', 'update', 'update_pricing', 'skip_pricing'])
    .default('skip')
    .transform((v) => (v === 'update' ? 'update_pricing' : v)),
  createMissingDevices: z.coerce.boolean().default(true),
});

export const migrationTicketHistoryOptionsSchema = z.object({
  ticketPrefix: z.string().min(1).max(20).default('RD-'),
  skipDuplicates: z.coerce.boolean().default(true),
});

export const migrationInvoiceImportOptionsSchema = z.object({
  ticketPrefix: z.string().min(1).max(20).default('RD-'),
  salePrefix: z.string().min(1).max(20).default('INV-'),
  onDuplicate: z.enum(['skip', 'update']).default('skip'),
  skipMissingTickets: z.coerce.boolean().default(true),
});

export const deviceCategorySchema = z.enum([
  'phone',
  'tablet',
  'laptop',
  'desktop',
  'console',
  'wearable',
  'watch',
  'other',
]);

export const createDeviceMakeSchema = z.object({
  name: z.string().trim().min(1).max(100),
  category: deviceCategorySchema.optional(),
  sortOrder: z.number().int().min(0).max(32767).optional(),
});

export const updateDeviceMakeSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    category: deviceCategorySchema.nullable().optional(),
    active: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(32767).optional(),
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'Provide at least one field to update' });

export const createDeviceModelSchema = z.object({
  makeId: z.string().uuid(),
  name: z.string().trim().min(1).max(150),
  category: deviceCategorySchema,
  releaseYear: z.number().int().min(1990).max(2100).nullable().optional(),
  estimatedValue: z.union([z.number().nonnegative(), z.string()]).nullable().optional(),
  sortOrder: z.number().int().min(0).max(32767).optional(),
});

export const updateDeviceModelSchema = z
  .object({
    name: z.string().trim().min(1).max(150).optional(),
    category: deviceCategorySchema.optional(),
    releaseYear: z.number().int().min(1990).max(2100).nullable().optional(),
    estimatedValue: z.union([z.number().nonnegative(), z.string()]).nullable().optional(),
    active: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(32767).optional(),
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'Provide at least one field to update' });

// ── REPAIR SERVICE SCOPE ──────────────────────────────────────────────────────
export const repairServiceScopeTypeSchema = z.enum([
  'per_model',
  'all_devices',
  'categories',
  'makes',
]);

const scopeFields = {
  scopeType: repairServiceScopeTypeSchema.optional(),
  scopeCategories: z.array(deviceCategorySchema).optional(),
  scopeMakeIds: z.array(z.string().uuid()).optional(),
  scopeExcludedModelIds: z.array(z.string().uuid()).optional(),
  flatPrice: z.number().nonnegative().nullable().optional(),
  flatEstimatedMinutes: z.number().int().positive().nullable().optional(),
  flatWarrantyDays: z.number().int().nonnegative().nullable().optional(),
};

function refineScopeFlatPrice<T extends {
  scopeType?: string;
  flatPrice?: number | null;
}>(data: T, ctx: z.RefinementCtx) {
  // Only enforce when scopeType is explicitly set (create defaults handled caller-side;
  // update may omit fields).
  if (
    data.scopeType != null &&
    data.scopeType !== 'per_model' &&
    (data.flatPrice == null || Number.isNaN(data.flatPrice))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'flatPrice is required when scope is not per_model',
      path: ['flatPrice'],
    });
  }
}

export const createRepairServiceSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    categorySlug: z.string().max(50).optional(),
    description: z.string().max(5000).optional().nullable(),
    defaultPrice: z.union([z.string(), z.number()]).optional().nullable(),
    defaultWarrantyDays: z.number().int().nonnegative().optional(),
    estimatedMinutes: z.number().int().positive().optional().nullable(),
    sortOrder: z.number().int().optional(),
    displayOnPos: z.boolean().optional(),
    displayOnPortal: z.boolean().optional(),
    displayOnWidget: z.boolean().optional(),
    ...scopeFields,
  })
  .superRefine((data, ctx) => {
    const scope = data.scopeType ?? 'per_model';
    if (scope !== 'per_model' && (data.flatPrice == null || Number.isNaN(data.flatPrice))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'flatPrice is required when scope is not per_model',
        path: ['flatPrice'],
      });
    }
  });

export const updateRepairServiceSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    categorySlug: z.string().max(50).optional().nullable(),
    description: z.string().max(5000).optional().nullable(),
    defaultPrice: z.union([z.string(), z.number()]).optional().nullable(),
    defaultWarrantyDays: z.number().int().nonnegative().optional().nullable(),
    estimatedMinutes: z.number().int().positive().optional().nullable(),
    loyaltyOverride: z.enum(['inherit', 'custom_rate', 'disabled']).optional(),
    loyaltyEarnRate: z.union([z.string(), z.number()]).optional().nullable(),
    sortOrder: z.number().int().optional(),
    displayOnPos: z.boolean().optional(),
    displayOnPortal: z.boolean().optional(),
    displayOnWidget: z.boolean().optional(),
    active: z.boolean().optional(),
    ...scopeFields,
  })
  .superRefine(refineScopeFlatPrice);

export const bulkSetRepairServiceScopeSchema = z
  .object({
    serviceIds: z.array(z.string().uuid()).min(1).max(100),
    scopeType: repairServiceScopeTypeSchema,
    scopeCategories: z.array(deviceCategorySchema).optional().default([]),
    scopeMakeIds: z.array(z.string().uuid()).optional().default([]),
    scopeExcludedModelIds: z.array(z.string().uuid()).optional().default([]),
    flatPrice: z.number().nonnegative().nullable().optional(),
    flatEstimatedMinutes: z.number().int().positive().nullable().optional(),
    flatWarrantyDays: z.number().int().nonnegative().nullable().optional(),
    /** When converting from per_model, soft-deactivate pricing rows */
    deactivatePricingRows: z.boolean().optional().default(true),
  })
  .superRefine(refineScopeFlatPrice);

export type CreateRepairServiceInput = z.infer<typeof createRepairServiceSchema>;
export type UpdateRepairServiceInput = z.infer<typeof updateRepairServiceSchema>;
export type BulkSetRepairServiceScopeInput = z.infer<typeof bulkSetRepairServiceScopeSchema>;

// ── CASH SESSIONS / DENOMINATIONS ─────────────────────────────────────────────
const nonNegInt = z.coerce.number().int().nonnegative();

const denominationUnitSchema = z.object({
  key: z.string().min(1).max(40),
  valueCents: z.coerce.number().int().positive(),
  label: z.string().min(1).max(40),
  enabled: z.boolean(),
});

export const cashDenominationConfigSchema = z.object({
  bills: z.array(denominationUnitSchema).optional(),
  coins: z.array(denominationUnitSchema).optional(),
  rollValues: z.record(z.string(), z.coerce.number().int().nonnegative()).optional(),
});

/** Count map: denom keys → quantity; optional rolls nested map. */
export const denominationCountsSchema = z
  .record(z.string(), z.union([nonNegInt, z.record(z.string(), nonNegInt)]))
  .superRefine((val, ctx) => {
    for (const [key, v] of Object.entries(val)) {
      if (key === 'rolls') {
        if (typeof v !== 'object' || v === null || Array.isArray(v)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'rolls must be an object' });
        }
        continue;
      }
      if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid quantity for ${key}`,
          path: [key],
        });
      }
    }
  });

export const cashCountMethodSchema = z.enum(['denominations', 'manual_total']);

export const openCashSessionSchema = z
  .object({
    countMethod: cashCountMethodSchema.optional(),
    openingFloat: z.coerce.number().nonnegative().optional(),
    openingDenominations: denominationCountsSchema.optional(),
    notes: z.string().max(1000).optional(),
    /** When true (default), send manager alerts if drawer health is not ok. */
    notifyManagers: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    const method =
      body.countMethod ??
      (body.openingDenominations != null ? 'denominations' : 'manual_total');
    if (method === 'manual_total') {
      if (body.openingFloat == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'openingFloat is required for manual_total',
          path: ['openingFloat'],
        });
      }
    } else if (!body.openingDenominations) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'openingDenominations is required for denomination count',
        path: ['openingDenominations'],
      });
    }
  });

export const closeCashSessionSchema = z
  .object({
    countMethod: cashCountMethodSchema.optional(),
    closingCount: z.coerce.number().nonnegative().optional(),
    closingDenominations: denominationCountsSchema.optional(),
    notes: z.string().max(1000).optional(),
    closingNotes: z.string().max(1000).optional(),
    managerOverrideAuthorizedBy: z.string().uuid().optional(),
    notifyManagers: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    const method =
      body.countMethod ??
      (body.closingDenominations != null ? 'denominations' : 'manual_total');
    if (method === 'manual_total') {
      if (body.closingCount == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'closingCount is required for manual_total',
          path: ['closingCount'],
        });
      }
    } else if (!body.closingDenominations) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'closingDenominations is required for denomination count',
        path: ['closingDenominations'],
      });
    }
  });

export {
  failureTypes,
  failureDispositions,
  managerDispositions,
  rmaItemStatuses,
  createWarrantyTicketSchema,
  recordInstalledPartFailureSchema,
  recordDamageReportSchema,
  reclassifyFailureSchema,
  quarantineBinSchema,
  damageReportFilterSchema,
  resolveRmaSchema,
} from './warranty-rework.js';
export type {
  CreateWarrantyTicketInput,
  RecordInstalledPartFailureInput,
  RecordDamageReportInput,
  ReclassifyFailureInput,
  ResolveRmaInput,
} from './warranty-rework.js';

export {
  inventoryMergeFields,
  mergeInventoryItemsSchema,
  previewInventoryMergeSchema,
} from './inventory-merge.js';
export type { MergeInventoryItemsInput } from './inventory-merge.js';

export {
  partsRequestQueueQuerySchema,
  createPosFromPartsRequestsSchema,
  allocatePartsRequestsSchema,
  cancelPartsRequestSchema,
} from './parts-requests.js';
export type {
  CreatePosFromPartsRequestsInput,
  AllocatePartsRequestsInput,
} from './parts-requests.js';

export type OpenCashSessionInput = z.infer<typeof openCashSessionSchema>;
export type CloseCashSessionInput = z.infer<typeof closeCashSessionSchema>;
export type DenominationCountsInput = z.infer<typeof denominationCountsSchema>;
export type CashDenominationConfigInput = z.infer<typeof cashDenominationConfigSchema>;
